import axios from 'axios';
import { regionFromPathname } from '../utils/regionFromPath';
import { localeFromRegion } from '../i18n';

const LIVE_API_BASE_URL = 'https://apisimilarmovies.vercel.app';
const envApiBase = process.env.REACT_APP_API_BASE_URL;
const baseURL =
  envApiBase && !/localhost|127\.0\.0\.1/i.test(String(envApiBase))
    ? envApiBase
    : LIVE_API_BASE_URL;
const AUTH_STORAGE_KEY = 'simimovies.auth.v1';

const ENV_SITE_KEY = (process.env.REACT_APP_SITE_KEY || 'default').trim().toLowerCase();

/**
 * Optional: map browser hostname → Website.key (same keys as Admin → Websites).
 * Build-time env `REACT_APP_SITE_HOST_MAP`, comma-separated `host:key` pairs, e.g.
 * `fliqmatch.com:fliqmatch,www.fliqmatch.com:fliqmatch`
 * `fliqmatch.com:fliqmatch,www.fliqmatch.com:fliqmatch` so Hostinger (fliqmatch.com) uses the correct Website key.
 */
function parseSiteHostMap() {
  const raw = process.env.REACT_APP_SITE_HOST_MAP;
  if (!raw || typeof raw !== 'string') return {};
  const out = {};
  String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((pair) => {
      const idx = pair.lastIndexOf(':');
      if (idx <= 0) return;
      const host = pair.slice(0, idx).trim().toLowerCase();
      const key = pair.slice(idx + 1).trim().toLowerCase();
      if (host && key) out[host] = key;
    });
  return out;
}

const SITE_HOST_MAP = parseSiteHostMap();

/** Website.key sent as X-Site (must match Admin → Websites and campaign website). */
export function resolveClientSiteKey() {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const h = String(window.location.hostname).trim().toLowerCase();
    if (SITE_HOST_MAP[h]) return SITE_HOST_MAP[h];
  }
  return ENV_SITE_KEY;
}

export const http = axios.create({
  baseURL,
  timeout: 45000,
  headers: { 'Content-Type': 'application/json', 'X-Site': ENV_SITE_KEY },
});

function getAuthToken() {
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.token ? String(parsed.token) : '';
  } catch {
    return '';
  }
}

/** Two-letter region segment from path (e.g. /gb/...); used for ad country when not set in the request body. */
export function getApiCountryCode() {
  if (typeof window === 'undefined') return '';
  const r = regionFromPathname(window.location.pathname);
  return r && /^[a-z]{2}$/.test(r) ? r : '';
}

/** BCP-47-ish UI locale from path region; sent as `X-Locale` for ad middleware (`parseAdContext`). */
export function getApiLocaleHeader() {
  if (typeof window === 'undefined') return '';
  const region = regionFromPathname(window.location.pathname);
  return String(localeFromRegion(region) || '').toLowerCase();
}

http.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers['X-Site'] = resolveClientSiteKey();
  if (typeof window !== 'undefined' && window.location?.hostname) {
    config.headers['X-Client-Host'] = String(window.location.hostname).toLowerCase();
  }
  const cc = getApiCountryCode();
  if (cc) config.headers['X-Country-Code'] = cc;
  const loc = getApiLocaleHeader();
  if (loc) config.headers['X-Locale'] = loc;
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
